import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Clock, Zap, Calendar, Lightbulb } from 'lucide-react';

const MaximizingAccessPassArticle = ({ onBack }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <button 
        onClick={onBack}
        className="flex items-center text-gray-600 hover:text-black mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Help Center
      </button>

      <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-6 rounded-lg mb-8">
        <div className="flex items-start">
          <CreditCard className="w-6 h-6 text-purple-500 mr-3 flex-shrink-0 mt-1" />
          <div>
            <h1 className="text-2xl font-bold mb-3">Maximizing Your Access Pass</h1>
            <p className="text-gray-700">
              Understand the various payment options, trial limitations, and strategies to get the most value 
              from your subscription. Learn efficient prototyping techniques and manage your access to optimize 
              your investment.
            </p>
          </div>
        </div>
      </div>

      {!expanded ? (
        <div>
          <p className="text-gray-700 mb-6">
            Our flexible access pass system gives you control over when and how you use our platform. 
            Whether you're on a trial, using a time-limited pass, or considering upgrading, 
            this guide will help you make the most of your access and maximize productivity.
          </p>
          <button
            onClick={() => setExpanded(true)}
            className="text-black font-medium hover:underline"
          >
            Read more
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <CreditCard className="w-5 h-5 mr-2 text-gray-700" />
              Understanding Your Access Options
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                We offer several flexible ways to access our platform:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Free Trial</strong> - New accounts start with a limited number of free generations to explore the platform.
                </li>
                <li>
                  <strong>Pay-as-you-go Access Passes</strong> - Purchase time-limited access (30, 60, 90, 180, or 360 days) with no auto-renewal.
                </li>
              </ul>
              <p className="mt-4">
                Our pay-as-you-go model means:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>No surprise charges or automatic renewals</li>
                <li>Access expires automatically after your chosen time period</li>
                <li>Buy a new pass only when you need it</li>
                <li>Unlimited prototype generations during your access period</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Clock className="w-5 h-5 mr-2 text-gray-700" />
              Planning Your Access Time
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                To maximize value from your access pass, consider your project timeline and workflow:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>For specific projects</strong> - Choose a pass duration that aligns with your project timeline. For a two-month project, a 60-day pass provides focused access.
                </li>
                <li>
                  <strong>For ongoing work</strong> - Longer passes (180 or 360 days) offer better value for teams with consistent prototyping needs.
                </li>
                <li>
                  <strong>For intermittent needs</strong> - Short passes (30 or 60 days) work well for occasional prototyping, with breaks between periods of active use.
                </li>
              </ul>
              <p className="mt-4 mb-4">
                Consider these factors when selecting your pass duration:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Project deadlines and milestones</li>
                <li>Team availability for review and feedback</li>
                <li>Stakeholder meeting schedules</li>
                <li>Budget limitations and approval cycles</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Zap className="w-5 h-5 mr-2 text-gray-700" />
              Maximizing Productivity
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Get the most out of your access by optimizing your workflow:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Batch your work</strong> - Create multiple views in one session rather than spacing them out.
                </li>
                <li>
                  <strong>Prepare descriptions in advance</strong> - Draft your view descriptions offline before starting generation.
                </li>
                <li>
                  <strong>Use templates and patterns</strong> - Once you've created a successful view, use similar patterns for related screens.
                </li>
                <li>
                  <strong>Collect feedback efficiently</strong> - Gather stakeholder input on multiple views at once to minimize revisions.
                </li>
                <li>
                  <strong>Export frequently</strong> - Download HTML for your finalized views to preserve access to your work.
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold flex items-center mb-4">
              <Calendar className="w-5 h-5 mr-2 text-gray-700" />
              Managing Your Access Timeline
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                Keep track of your access status to avoid interruptions:
              </p>
              <ul className="list-disc pl-5 space-y-3">
                <li>
                  <strong>Check your account page</strong> to view your current access status and expiration date.
                </li>
                <li>
                  <strong>Plan renewal timing</strong> - Consider purchasing a new pass a few days before expiration if you have ongoing work.
                </li>
                <li>
                  <strong>Export key assets</strong> as your pass nears expiration to ensure continuity.
                </li>
                <li>
                  <strong>Time your purchases strategically</strong> - Align new passes with project phases or sprint cycles.
                </li>
              </ul>
              <p className="mt-4">
                Remember that you can always view your projects and their details even without an active pass, 
                but you won't be able to create new views or revise existing ones until you purchase a new pass.
              </p>
            </div>
          </section>

          <section className="bg-gray-50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
              Tips for Teams
            </h2>
            <div className="text-gray-700">
              <p className="mb-4">
                If multiple team members are working on prototypes:
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Coordinate access timing</strong> - Schedule passes to overlap during collaborative phases.</li>
                <li><strong>Share account login</strong> - Consider sharing access (where allowed by your organization) for cost-efficiency.</li>
                <li><strong>Plan intensive sprints</strong> - Schedule focused design sprints to maximize productivity during pass periods.</li>
                <li><strong>Document work thoroughly</strong> - Maintain detailed documentation that team members can reference even without active access.</li>
                <li><strong>Designate a prototype owner</strong> - Have one team member responsible for maintaining and updating prototypes.</li>
              </ul>
            </div>
          </section>

          <button
            onClick={() => setExpanded(false)}
            className="text-black font-medium hover:underline mt-4 block"
          >
            Show less
          </button>
        </div>
      )}
    </div>
  );
};

export default MaximizingAccessPassArticle;